import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  ShieldCheckIcon,
  ShieldExclamationIcon,
  KeyIcon,
  ArrowPathIcon,
  LockOpenIcon,
  XMarkIcon,
  ComputerDesktopIcon,
} from '@heroicons/react/24/outline';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

/**
 * Read-only security posture for one user, plus the three support actions a
 * Zappy admin is allowed to take.
 *
 * Every action here is LINK-ONLY or destructive-to-access — never a credential.
 * There is deliberately no "set password" affordance: an admin who can read a
 * customer's password can impersonate them without a trace, and the audit trail
 * below is worth nothing if that path exists.
 *
 * The server enforces all of this independently (`requireRecentAuth`,
 * `requireOwnMfa`); this component only has to explain the outcome.
 */
const UserSecurityModal = ({ user, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/api/admin/users/${user.userId}/security`);
      setData(response.data);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to load security information');
    } finally {
      setLoading(false);
    }
  }, [user.userId]);

  useEffect(() => { load(); }, [load]);

  /**
   * A 401 REAUTH_REQUIRED here is not a session failure — it means the admin's
   * sign-in is too old for a security-critical action. Saying "please sign in
   * again" would send them hunting for a broken session that is working fine.
   */
  const handleError = (error, fallback) => {
    const code = error.response?.data?.code;
    if (code === 'REAUTH_REQUIRED') {
      toast.error('For security, sign out and back in before making this change.');
      return;
    }
    if (code === 'ACTOR_MFA_REQUIRED') {
      toast.error('Enable two-factor authentication on your own admin account first.');
      return;
    }
    toast.error(error.response?.data?.error || fallback);
  };

  const act = async (key, url, body, successMessage, confirmMessage) => {
    if (confirmMessage && !window.confirm(confirmMessage)) return;
    setBusy(key);
    try {
      await axios.post(url, body || {});
      toast.success(successMessage);
      await load();
    } catch (error) {
      handleError(error, 'Action failed');
    } finally {
      setBusy(null);
    }
  };

  const base = `${API_BASE_URL}/api/admin/users/${user.userId}`;
  const locked = data?.user?.lockedUntil && new Date(data.user.lockedUntil) > new Date();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="p-4 border-b flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold">Security — {user.name || user.email}</h2>
            <p className="text-sm text-gray-500">{user.email}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded" aria-label="Close">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="p-4 overflow-y-auto space-y-6">
          {loading && <div className="text-center text-gray-500 py-8">Loading…</div>}

          {!loading && data && (
            <>
              {/* Posture */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Stat
                  label="Password"
                  value={data.user.hasPassword ? 'Set' : 'None'}
                  good={data.user.hasPassword}
                />
                <Stat
                  label="Two-factor"
                  value={data.factors.some((f) => f.verified) ? 'Enabled' : 'Off'}
                  good={data.factors.some((f) => f.verified)}
                />
                <Stat
                  label="Email verified"
                  value={data.user.emailVerifiedAt ? 'Yes' : 'No'}
                  good={!!data.user.emailVerifiedAt}
                />
                <Stat
                  label="Sign-in methods"
                  value={
                    [
                      data.user.hasPassword && 'Password',
                      data.user.providers.google && 'Google',
                      data.user.providers.apple && 'Apple',
                      data.user.providers.facebook && 'Facebook',
                    ].filter(Boolean).join(', ') || 'None'
                  }
                />
              </div>

              {locked && (
                <div className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded p-3">
                  <div className="text-sm text-amber-800">
                    Locked after {data.user.failedLoginCount} failed attempts until{' '}
                    {new Date(data.user.lockedUntil).toLocaleString()}.
                  </div>
                  <button
                    disabled={busy === 'unlock'}
                    onClick={() => act('unlock', `${base}/unlock`, null, 'Account unlocked')}
                    className="btn-secondary text-sm inline-flex items-center gap-1"
                  >
                    <LockOpenIcon className="h-4 w-4" /> Unlock
                  </button>
                </div>
              )}

              {/* Actions */}
              <div>
                <h3 className="font-medium mb-2">Support actions</h3>
                <div className="space-y-2">
                  <ActionRow
                    icon={<KeyIcon className="h-5 w-5 text-gray-400" />}
                    title="Send a password reset link"
                    // Stated explicitly so nobody goes looking for a way to set
                    // one — the absence of that button is the design.
                    description="Emails the user a one-time link. You never see or set their password."
                    actionLabel="Send link"
                    busy={busy === 'reset'}
                    disabled={!data.user.email}
                    onAction={() => act(
                      'reset',
                      `${base}/password-reset`,
                      { revokeSessions: false },
                      'Reset link sent'
                    )}
                  />
                  <ActionRow
                    icon={<ComputerDesktopIcon className="h-5 w-5 text-gray-400" />}
                    title="Sign out everywhere"
                    description={`Ends all ${data.sessions.length} active session(s) on every device.`}
                    actionLabel="Revoke sessions"
                    busy={busy === 'revoke'}
                    onAction={() => act(
                      'revoke',
                      `${base}/sessions/revoke`,
                      null,
                      'All sessions revoked',
                      'End every active session for this user?'
                    )}
                  />
                  <ActionRow
                    icon={<ShieldExclamationIcon className="h-5 w-5 text-amber-500" />}
                    title="Reset two-factor authentication"
                    description="Lost-device recovery. Removes every factor and recovery code, ends all sessions, and emails the user. Requires 2FA on your own admin account."
                    actionLabel="Reset 2FA"
                    danger
                    busy={busy === 'mfa'}
                    disabled={!data.factors.some((f) => f.verified)}
                    onAction={() => act(
                      'mfa',
                      `${base}/mfa/reset`,
                      null,
                      'Two-factor authentication reset',
                      'Remove every second factor for this user? They will be emailed, and must set 2FA up again.'
                    )}
                  />
                </div>
              </div>

              {/* Factors */}
              <div>
                <h3 className="font-medium mb-2">Two-factor methods</h3>
                {data.factors.length === 0 ? (
                  <p className="text-sm text-gray-500">No methods enrolled.</p>
                ) : (
                  <ul className="text-sm divide-y border rounded">
                    {data.factors.map((f) => (
                      <li key={f.id} className="px-3 py-2 flex items-center justify-between">
                        <span className="capitalize">
                          {f.type}
                          {/* Destinations arrive already masked from the server. */}
                          {f.destination ? ` — ${f.destination}` : ''}
                        </span>
                        <span className={f.verified ? 'text-green-600' : 'text-gray-400'}>
                          {f.verified ? 'Verified' : 'Pending'}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  {data.recoveryCodesRemaining} unused recovery code(s).
                </p>
              </div>

              {/* Sessions */}
              <div>
                <h3 className="font-medium mb-2">Active sessions ({data.sessions.length})</h3>
                {data.sessions.length === 0 ? (
                  <p className="text-sm text-gray-500">No active sessions.</p>
                ) : (
                  <ul className="text-sm divide-y border rounded max-h-40 overflow-y-auto">
                    {data.sessions.map((s) => (
                      <li key={s.id} className="px-3 py-2">
                        <div className="flex items-center justify-between">
                          <span className="text-gray-700">
                            {s.surface} · {(s.amr || []).join(', ') || 'unknown'}
                            {s.mfaSatisfied && (
                              <ShieldCheckIcon className="h-3 w-3 inline ml-1 text-green-600" />
                            )}
                          </span>
                          <span className="text-gray-400 text-xs">
                            {new Date(s.issuedAt).toLocaleString()}
                          </span>
                        </div>
                        <div className="text-xs text-gray-400 truncate">
                          {s.ip} · {s.userAgent}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Audit */}
              <div>
                <h3 className="font-medium mb-2 flex items-center gap-2">
                  Security audit trail
                  <button onClick={load} className="text-gray-400 hover:text-gray-600" aria-label="Refresh">
                    <ArrowPathIcon className="h-4 w-4" />
                  </button>
                </h3>
                {data.audit.length === 0 ? (
                  <p className="text-sm text-gray-500">Nothing recorded yet.</p>
                ) : (
                  <ul className="text-sm divide-y border rounded max-h-56 overflow-y-auto">
                    {data.audit.map((entry) => (
                      <li key={entry.id} className="px-3 py-2 flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="font-mono text-xs text-gray-700">{entry.event}</div>
                          {/* An entry where the actor differs from the subject
                              is an admin acting on someone else — the case the
                              trail exists to make reconstructable. */}
                          {entry.actor_user_id && entry.actor_user_id !== entry.user_id && (
                            <div className="text-xs text-amber-700">
                              by admin {entry.actor_user_id}
                            </div>
                          )}
                          <div className="text-xs text-gray-400 truncate">{entry.ip}</div>
                        </div>
                        <span className="text-xs text-gray-400 whitespace-nowrap">
                          {new Date(entry.created_at).toLocaleString()}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const Stat = ({ label, value, good }) => (
  <div className="border rounded p-3">
    <div className="text-xs text-gray-500">{label}</div>
    <div className={`text-sm font-medium ${good === true ? 'text-green-700' : good === false ? 'text-gray-700' : 'text-gray-900'}`}>
      {value}
    </div>
  </div>
);

const ActionRow = ({ icon, title, description, actionLabel, onAction, busy, danger, disabled }) => (
  <div className="flex items-start justify-between gap-4 border rounded p-3">
    <div className="flex gap-3 min-w-0">
      <div className="mt-0.5">{icon}</div>
      <div className="min-w-0">
        <div className="text-sm font-medium text-gray-900">{title}</div>
        <div className="text-xs text-gray-500">{description}</div>
      </div>
    </div>
    <button
      onClick={onAction}
      disabled={busy || disabled}
      className={`text-sm px-3 py-1.5 rounded whitespace-nowrap disabled:opacity-50 ${
        danger ? 'bg-amber-600 text-white hover:bg-amber-700' : 'bg-gray-100 hover:bg-gray-200'
      }`}
    >
      {busy ? 'Working…' : actionLabel}
    </button>
  </div>
);

export default UserSecurityModal;
